import { buttonVariants } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';
import { buttonClassName, parseButtonClasses } from '~/lib/buttonStyles';
import { cn } from '~/lib/utils';
import { type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

type ButtonProps = {
  label?: React.ReactNode;
  fullWidth?: boolean;
  isLoading?: boolean;
  spinnerClassName?: string;
  icon?: React.ReactNode;
  asChild?: boolean;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: VariantProps<typeof buttonVariants>['size'];
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export default function Button({
  label,
  fullWidth,
  className,
  isLoading,
  spinnerClassName,
  disabled,
  icon,
  asChild = false,
  variant,
  size,
  children,
  ...others
}: ButtonProps) {
  const parsed = parseButtonClasses(className);
  const resolvedVariant = variant ?? parsed.variant;
  const resolvedSize = size ?? (parsed.circle ? 'icon' : parsed.size);
  const Comp = asChild ? Slot : 'button';
  const isIconSize =
    resolvedSize === 'icon' || resolvedSize === 'icon-xs' || resolvedSize === 'icon-sm' || resolvedSize === 'icon-lg';
  const content = children ?? label;

  return (
    <Comp
      data-slot="button"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      className={cn(
        buttonVariants({ variant: resolvedVariant, size: resolvedSize }),
        parsed.circle && 'rounded-full',
        'cursor-pointer normal-case',
        fullWidth && 'w-full',
        parsed.className,
      )}
      disabled={isLoading || disabled}
      {...others}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading ? <Spinner data-icon="inline-start" className={spinnerClassName} /> : icon}
          {isIconSize && isLoading ? null : content}
        </>
      )}
    </Comp>
  );
}

export { buttonClassName };
